/**
 * Every word the tour explains itself with, in one place — rewriting the tour
 * means editing this file and nothing else. Welcome.jsx owns the mechanics
 * (which step checks itself off from what, where each button goes); this file
 * owns only the words.
 *
 * The rules, all of them:
 *   - Keep the seven keys and their order; they are what Welcome.jsx wires
 *     the buttons and checkmarks to.
 *   - `title` is the step's heading. `body` is a list of strings, one per
 *     paragraph — add or remove paragraphs freely. Each entry is a WHOLE
 *     paragraph: a sentence split across two of them becomes two paragraphs
 *     broken mid-thought, which is the one mistake this shape invites.
 *   - Where a paragraph is too long for one line, join the pieces with `+` and
 *     put the space at the START of the next piece, never at the end of the
 *     previous one. Both, and the sentence gets a double space.
 *   - `cta` is the text on the step's button (the destination is fixed in
 *     Welcome.jsx).
 *   - In `done.help`, the literal token {link} marks where the tappable link
 *     to the manual goes, and `helpLinkLabel` is that link's text.
 *   - Strings are single-quoted: a straight apostrophe inside one needs a
 *     backslash (\'), or just use the curly ’ like the current text does.
 *
 * After editing: `npm run build` (or the dev server picks it up live), then
 * open /welcome to read it in place.
 */
export const TOUR_COPY = {
  welcome: {
    title: 'Welcome to Pantry Pro',
    body: [
      'This app can keep track of the food you purchased, what is in your'
      + ' pantry now, and what you can cook now. Manage your recipes, find new'
      + ' recipes, and share the app with others in your household.',
      'The next six screens each detail one part of the app, and how to'
      + ' use the app. Add Food, Add Recipes, Cook, Plan/Shop, and Share.'
      + ' The home screen keeps a link back here until you finish or skip.',
    ],
  },

  stock: {
    title: 'Add your Food',
    body: [
      'There are four ways to add: photograph a receipt, photograph an open shelf, scan a'
      + ' barcode, or manually type in.',
      'The photo options use one monthly scan allowance, because photos go to'
      + ' an AI vision model to be read. Barcodes and typing require no AI and are'
      + ' unlimited.',
      'Output from a photo scan has to be approved by you before the pantry is updated.',
    ],
    cta: 'Open the scan screen',
  },

  recipes: {
    title: 'Add Recipes',
    body: [
      'Import recipes from a link, from a photo, or from text you paste in.'
      + ' Or search for a new recipe from the curated list of websites.',
      'Photos and uploaded pictures of recipes use one AI scan credit.'
      + ' Searching the curated list, importing from a link, and typing'
      + ' recipe text does not use AI.',
    ],
    cta: 'Find a recipe',
  },

  cook: {
    title: 'Cook what you have',
    body: [
      'Cook Now ranks your saved recipes by what is on the shelves right now, and'
      + ' you can point it at the ingredients you want used up first.',
      'When the units cannot be compared — a recipe wanting two cloves against a'
      + ' head of garlic in the fridge — it says “can’t tell” rather than'
      + ' guessing.',
      'An ingredient it could not match at all offers you the near misses it'
      + ' found. Tapping one teaches the app that this is your word for that'
      + ' thing, and it stays taught.',
    ],
    cta: 'See what you can cook',
  },

  shop: {
    title: 'Plan and shop',
    body: [
      'The shopping list fills itself from two places: what a recipe is missing,'
      + ' and what has run low against the level you set for an item.',
      'The meal plan feeds the same list, so a week planned on Sunday becomes one'
      + ' shopping list rather than four.',
    ],
    cta: 'Open the shopping list',
  },

  share: {
    title: 'Share it',
    body: [
      'Pantry-Pro is built to include the entire household by design.'
      + ' Invite other users to collaborate on the same household from'
      + ' the invitation section of the Settings.',
    ],
    cta: 'Open Settings',
  },

  done: {
    title: 'That is the tour',
    body: [
      'Nothing above has to be finished today. An empty pantry with two recipes in'
      + ' it is a perfectly reasonable place to start from.',
    ],
    help: 'The longer explanation of every screen lives in {link}, including what'
      + ' the plan covers and how to get your data back out.',
    helpLinkLabel: 'Settings → Help',
  },
};
